import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain, Wand2, Calendar as CalIcon, FileText, Loader2, Sparkles,
  TrendingUp, AlertTriangle, CheckCircle2, X, RefreshCcw, Copy,
  ChevronRight, Lightbulb, Target, Megaphone, Rocket, Eye, Trash2
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';

const fmtCLP = (n) => '$ ' + Number(n || 0).toLocaleString('es-CL');

const CerebroIA = () => {
  const [subtab, setSubtab] = useState('wizard');
  const [meta, setMeta] = useState(null);
  const [borradoresKey, setBorradoresKey] = useState(0);  // bust cache para refetch al navegar tras crear

  useEffect(() => { api.get('/campanas-ia/metadata').then(r => setMeta(r.data)).catch(() => {}); }, []);

  const irABorradores = () => {
    setBorradoresKey(k => k + 1);
    setSubtab('borradores');
  };

  return (
    <div className="space-y-4" data-testid="cerebro-ia">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Cerebro IA · Campañas</h2>
          <p className="text-sm text-slate-600">Generación proactiva + aprendizaje autónomo + calendario estratégico anual</p>
        </div>
      </div>

      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="wizard" data-testid="cia-tab-wizard"><Wand2 className="w-3.5 h-3.5 mr-1" /> Wizard</TabsTrigger>
          <TabsTrigger value="aprendizaje" data-testid="cia-tab-aprendizaje"><Brain className="w-3.5 h-3.5 mr-1" /> Aprendizaje</TabsTrigger>
          <TabsTrigger value="calendario" data-testid="cia-tab-calendario"><CalIcon className="w-3.5 h-3.5 mr-1" /> Calendario</TabsTrigger>
          <TabsTrigger value="borradores" data-testid="cia-tab-borradores"><FileText className="w-3.5 h-3.5 mr-1" /> Borradores</TabsTrigger>
        </TabsList>

        <TabsContent value="wizard"><WizardPanel meta={meta} onCreated={irABorradores} /></TabsContent>
        <TabsContent value="aprendizaje"><AprendizajePanel /></TabsContent>
        <TabsContent value="calendario"><CalendarioPanel /></TabsContent>
        <TabsContent value="borradores"><BorradoresPanel refreshKey={borradoresKey} /></TabsContent>
      </Tabs>
    </div>
  );
};

/* ===================== WIZARD ===================== */
const WizardPanel = ({ meta, onCreated }) => {
  const [form, setForm] = useState({
    objetivo: '', pilar: '', presupuesto_clp: 500000, duracion_dias: 14,
    contexto_libre: '', publico_descripcion: '', aplicar_exclusiones_aprendizaje: true
  });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);

  const generar = async () => {
    if (!form.objetivo || !form.pilar) {
      toast.error('Selecciona objetivo y pilar');
      return;
    }
    setGenerating(true);
    try {
      const r = await api.post('/campanas-ia/wizard/generar', form);
      setResult(r.data);
      toast.success('Campaña generada con IA');
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setGenerating(false);
    }
  };

  const guardarBorrador = async () => {
    if (!result?.ia) return;
    const ia = result.ia;
    try {
      await api.post('/campanas-ia/borradores', {
        nombre: ia.nombre_campana_sugerido || `${form.objetivo} - ${form.pilar}`,
        objetivo: form.objetivo,
        pilar: form.pilar,
        presupuesto_clp: ia.presupuesto_sugerido_clp || form.presupuesto_clp,
        duracion_dias: form.duracion_dias,
        copys: ia.copys || [],
        segmentacion: ia.segmentacion || {},
        formato_sugerido: ia.formato_sugerido || 'Imagen única',
        ubicaciones: ia.ubicaciones_meta || [],
        palabras_clave_negativas: ia.palabras_clave_negativas_aplicadas || [],
        listas_exclusion: result.exclusiones_aplicadas?.perfiles || [],
        contexto_legal_usado: ia.contexto_legal_usado || '',
        estado: 'Borrador',
      });
      toast.success('Guardado como borrador');
      setResult(null);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  return (
    <Card className="p-5 border-slate-200">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>Objetivo de negocio *</Label>
          <Select value={form.objetivo} onValueChange={(v) => setForm({ ...form, objetivo: v })}>
            <SelectTrigger data-testid="wiz-objetivo"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
            <SelectContent>
              {(meta?.objetivos || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Pilar *</Label>
          <Select value={form.pilar} onValueChange={(v) => setForm({ ...form, pilar: v })}>
            <SelectTrigger data-testid="wiz-pilar"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
            <SelectContent>
              {(meta?.pilares || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Presupuesto CLP</Label>
          <Input type="number" value={form.presupuesto_clp} onChange={(e) => setForm({ ...form, presupuesto_clp: Number(e.target.value) })} data-testid="wiz-presupuesto" />
        </div>
        <div>
          <Label>Duración (días)</Label>
          <Input type="number" value={form.duracion_dias} onChange={(e) => setForm({ ...form, duracion_dias: Number(e.target.value) })} />
        </div>
        <div className="md:col-span-2">
          <Label>Público objetivo (descripción libre)</Label>
          <Input placeholder="Ej: dueños de PYMES en Santiago, contadores generales..." value={form.publico_descripcion} onChange={(e) => setForm({ ...form, publico_descripcion: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <Label>Contexto extra para la IA</Label>
          <Textarea rows={2} placeholder="Promociones, urgencia, eventos, hooks específicos..." value={form.contexto_libre} onChange={(e) => setForm({ ...form, contexto_libre: e.target.value })} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.aplicar_exclusiones_aprendizaje} onChange={(e) => setForm({ ...form, aplicar_exclusiones_aprendizaje: e.target.checked })} />
          Aplicar exclusiones aprendidas automáticamente
        </label>
        <Button onClick={generar} disabled={generating} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white" data-testid="wiz-generar">
          {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando con IA...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generar con IA</>}
        </Button>
      </div>

      {result && (
        <div className="mt-6 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            {(result.ia.copys || []).map((c, i) => (
              <Card key={i} className="p-3 border-indigo-200 bg-indigo-50/40">
                <Badge className="mb-2 bg-indigo-600 text-white">{c.tipo}</Badge>
                <p className="font-bold text-slate-900 text-sm leading-tight mb-1">{c.headline}</p>
                <p className="text-xs text-slate-700 mb-2">{c.body}</p>
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs">{c.cta}</Badge>
                  <Button size="sm" variant="ghost" className="h-6" onClick={() => { navigator.clipboard.writeText(`${c.headline}\n\n${c.body}\n\nCTA: ${c.cta}`); toast.success('Copiado'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          <Card className="p-4 border-slate-200">
            <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2"><Target className="w-4 h-4 text-rose-500" /> Segmentación sugerida</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div><span className="text-slate-500">Edad:</span> {result.ia.segmentacion?.edad_min}-{result.ia.segmentacion?.edad_max}</div>
              <div><span className="text-slate-500">Géneros:</span> {(result.ia.segmentacion?.generos || []).join(', ')}</div>
              <div><span className="text-slate-500">Formato:</span> {result.ia.formato_sugerido}</div>
              <div className="col-span-full"><span className="text-slate-500">Ubicaciones CL:</span> {(result.ia.segmentacion?.ubicaciones_cl || []).join(', ')}</div>
              <div className="col-span-full"><span className="text-slate-500">Intereses:</span> {(result.ia.segmentacion?.intereses || []).join(', ')}</div>
              <div className="col-span-full"><span className="text-slate-500">Cargos B2B:</span> {(result.ia.segmentacion?.cargos_b2b || []).join(', ')}</div>
              <div className="col-span-full"><span className="text-slate-500">Industrias:</span> {(result.ia.segmentacion?.industrias || []).join(', ')}</div>
              <div className="col-span-full"><span className="text-slate-500">Ubicaciones Meta:</span> {(result.ia.ubicaciones_meta || []).join(' · ')}</div>
            </div>
            {result.ia.presupuesto_sugerido_clp && (
              <p className="text-xs text-slate-600 mt-3"><span className="font-semibold">Presupuesto IA:</span> {fmtCLP(result.ia.presupuesto_sugerido_clp)} — {result.ia.razon_presupuesto}</p>
            )}
            {result.ia.contexto_legal_usado && (
              <div className="mt-3 p-2 bg-amber-50 border-l-2 border-amber-400 text-xs text-amber-900">
                <Lightbulb className="w-3 h-3 inline mr-1" /> {result.ia.contexto_legal_usado}
              </div>
            )}
            {(result.ia.palabras_clave_negativas_aplicadas || []).length > 0 && (
              <p className="text-xs text-slate-600 mt-3"><span className="font-semibold">Exclusiones aplicadas:</span> {result.ia.palabras_clave_negativas_aplicadas.join(', ')}</p>
            )}
          </Card>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setResult(null)}>Descartar</Button>
            <Button onClick={guardarBorrador} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wiz-guardar-borrador">
              <FileText className="w-4 h-4 mr-2" /> Guardar como Borrador
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

/* ===================== APRENDIZAJE ===================== */
const AprendizajePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analizando, setAnalizando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/campanas-ia/aprendizaje/ultimo');
      setData(r.data?.existe ? r.data : null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const reanalizar = async () => {
    setAnalizando(true);
    try {
      const r = await api.post('/campanas-ia/aprendizaje/analizar');
      setData(r.data);
      toast.success('Aprendizaje actualizado');
    } catch (e) {
      toast.error(formatErrorMessage(e));
    } finally {
      setAnalizando(false);
    }
  };

  if (loading) return <Card className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></Card>;

  if (!data) {
    return (
      <Card className="p-8 text-center border-slate-200">
        <Brain className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-900 mb-1">Sin aprendizaje aún</h3>
        <p className="text-sm text-slate-600 mb-4">
          Califica o descarta al menos 5 leads en el Pipeline para que el Cerebro IA pueda detectar patrones.
        </p>
        <Button onClick={reanalizar} disabled={analizando} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="apr-analizar">
          {analizando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando con Claude...</> : <><Sparkles className="w-4 h-4 mr-2" /> Ejecutar análisis ahora</>}
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs opacity-80">Reporte de aprendizaje · {data.modelo}</p>
            <p className="text-base font-semibold leading-snug">{data.resumen_ejecutivo}</p>
            <p className="text-xs opacity-80 mt-1">
              Dataset: {data.dataset_size?.calificados} calificados · {data.dataset_size?.descartados} descartados ·
              Generado: {new Date(data.generado_at).toLocaleString('es-CL')}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={reanalizar} disabled={analizando}>
            {analizando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
            <span className="ml-1">Re-analizar</span>
          </Button>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        <Card className="p-4 border-emerald-200 bg-emerald-50/30">
          <h4 className="font-semibold flex items-center gap-2 text-emerald-900 mb-2"><CheckCircle2 className="w-4 h-4" /> Patrones de éxito</h4>
          <div className="space-y-2">
            {(data.patrones_exito || []).map((p, i) => (
              <div key={i} className="bg-white p-2 rounded border border-emerald-100">
                <p className="text-sm font-semibold text-slate-900">{p.patron} <Badge variant="outline" className="ml-1 text-xs">{p.confianza_pct}%</Badge></p>
                <p className="text-xs text-slate-600 mt-0.5">{p.evidencia}</p>
              </div>
            ))}
            {(data.patrones_exito || []).length === 0 && <p className="text-xs text-slate-500">Sin patrones detectados aún.</p>}
          </div>
        </Card>

        <Card className="p-4 border-rose-200 bg-rose-50/30">
          <h4 className="font-semibold flex items-center gap-2 text-rose-900 mb-2"><AlertTriangle className="w-4 h-4" /> Patrones de fracaso</h4>
          <div className="space-y-2">
            {(data.patrones_fracaso || []).map((p, i) => (
              <div key={i} className="bg-white p-2 rounded border border-rose-100">
                <p className="text-sm font-semibold text-slate-900">{p.patron} <Badge variant="outline" className="ml-1 text-xs">{p.confianza_pct}%</Badge></p>
                <p className="text-xs text-slate-600 mt-0.5">{p.evidencia}</p>
              </div>
            ))}
            {(data.patrones_fracaso || []).length === 0 && <p className="text-xs text-slate-500">Sin patrones detectados aún.</p>}
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <Card className="p-4 border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-2 text-sm">Palabras clave negativas</h4>
          <div className="flex flex-wrap gap-1.5">
            {(data.palabras_clave_negativas || []).map((p, i) => <Badge key={i} className="bg-rose-100 text-rose-800 border-rose-200">{p}</Badge>)}
            {(data.palabras_clave_negativas || []).length === 0 && <p className="text-xs text-slate-500">Ninguna aún.</p>}
          </div>
        </Card>

        <Card className="p-4 border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-2 text-sm">Listas de exclusión</h4>
          <div className="space-y-1">
            {(data.listas_exclusion || []).map((l, i) => (
              <p key={i} className="text-xs"><Badge variant="outline" className="mr-1 text-xs">{l.tipo}</Badge>{l.valor} <span className="text-slate-500">— {l.razon}</span></p>
            ))}
            {(data.listas_exclusion || []).length === 0 && <p className="text-xs text-slate-500">Ninguna aún.</p>}
          </div>
        </Card>
      </div>

      <Card className="p-4 border-slate-200">
        <h4 className="font-semibold text-slate-900 mb-2 text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> Recomendaciones creativas</h4>
        <div className="space-y-2">
          {(data.recomendaciones_creativas || []).map((r, i) => (
            <div key={i} className="p-2 bg-amber-50 border-l-2 border-amber-400">
              <p className="text-sm font-semibold text-slate-900">{r.insight}</p>
              <p className="text-xs text-slate-700 mt-0.5">→ {r.accion}</p>
            </div>
          ))}
          {(data.recomendaciones_creativas || []).length === 0 && <p className="text-xs text-slate-500">Sin recomendaciones aún.</p>}
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        <Card className="p-3 border-rose-200">
          <p className="text-xs text-slate-500 mb-1">Campañas a recortar</p>
          <div className="space-y-1">
            {(data.campanas_a_recortar || []).map((c, i) => <Badge key={i} className="bg-rose-100 text-rose-800">{c}</Badge>)}
            {(data.campanas_a_recortar || []).length === 0 && <p className="text-xs text-slate-400">—</p>}
          </div>
        </Card>
        <Card className="p-3 border-emerald-200">
          <p className="text-xs text-slate-500 mb-1">Campañas a reforzar</p>
          <div className="space-y-1">
            {(data.campanas_a_reforzar || []).map((c, i) => <Badge key={i} className="bg-emerald-100 text-emerald-800">{c}</Badge>)}
            {(data.campanas_a_reforzar || []).length === 0 && <p className="text-xs text-slate-400">—</p>}
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ===================== CALENDARIO ===================== */
const CalendarioPanel = () => {
  const [data, setData] = useState(null);
  const [view, setView] = useState('mensual'); // mensual / trimestral
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/campanas-ia/calendario').then(r => { setData(r.data); setLoading(false); });
  }, []);

  if (loading) return <Card className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></Card>;
  if (!data) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900">Calendario {data.anio}</h3>
        <div className="flex gap-1">
          <Button size="sm" variant={view === 'mensual' ? 'default' : 'outline'} onClick={() => setView('mensual')} data-testid="cal-mensual">Mensual</Button>
          <Button size="sm" variant={view === 'trimestral' ? 'default' : 'outline'} onClick={() => setView('trimestral')} data-testid="cal-trimestral">Trimestral</Button>
        </div>
      </div>

      {view === 'mensual' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {data.meses.map(m => <MesCard key={m.mes_num} mes={m} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.trimestres.map((q, i) => (
            <Card key={i} className="p-4 border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-bold text-slate-900">{q.trimestre}</h4>
                <p className="text-xs text-slate-500">{q.meses.join(' · ')}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-2">
                <div className="p-2 bg-emerald-50 rounded">
                  <p className="text-2xl font-bold text-emerald-700">{q.total_activas}</p>
                  <p className="text-xs text-emerald-600">Activas</p>
                </div>
                <div className="p-2 bg-blue-50 rounded">
                  <p className="text-2xl font-bold text-blue-700">{q.total_planificadas}</p>
                  <p className="text-xs text-blue-600">Planificadas</p>
                </div>
                <div className="p-2 bg-purple-50 rounded">
                  <p className="text-2xl font-bold text-purple-700">{q.total_sugeridas}</p>
                  <p className="text-xs text-purple-600">Sugeridas IA</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {q.pilares_clave.map((p, j) => <Badge key={j} variant="outline" className="text-xs">{p}</Badge>)}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const MesCard = ({ mes }) => {
  const isCurrent = mes.mes_num === (new Date().getMonth() + 1);
  return (
    <Card className={`p-3 border ${isCurrent ? 'border-indigo-400 bg-indigo-50/40 shadow-md' : 'border-slate-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold text-slate-900">{mes.mes_nombre}</h4>
        {isCurrent && <Badge className="bg-indigo-600 text-white text-xs">Actual</Badge>}
      </div>
      <div className="space-y-2">
        <div>
          <p className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">Sugeridas IA</p>
          <div className="space-y-0.5 mt-1">
            {mes.campanas_sugeridas_ia.map((c, i) => (
              <p key={i} className="text-xs text-slate-700 flex items-start gap-1">
                <Sparkles className="w-3 h-3 text-purple-500 flex-shrink-0 mt-0.5" /> {c}
              </p>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {mes.pilares_estacionales.map((p, i) => <Badge key={i} variant="outline" className="text-[10px]">{p}</Badge>)}
        </div>
        {mes.activas.length > 0 && (
          <div className="pt-1 border-t border-slate-100">
            <p className="text-[10px] uppercase text-emerald-700 font-semibold">{mes.activas.length} activas</p>
            {mes.activas.slice(0, 3).map((a, i) => <p key={i} className="text-xs text-slate-600 truncate">• {a.nombre}</p>)}
          </div>
        )}
        {mes.planificadas.length > 0 && (
          <div className="pt-1 border-t border-slate-100">
            <p className="text-[10px] uppercase text-blue-700 font-semibold">{mes.planificadas.length} planificadas</p>
            {mes.planificadas.slice(0, 3).map((b, i) => <p key={i} className="text-xs text-slate-600 truncate">• {b.nombre}</p>)}
          </div>
        )}
      </div>
    </Card>
  );
};

/* ===================== BORRADORES ===================== */
const BorradoresPanel = ({ refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detalleId, setDetalleId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/campanas-ia/borradores');
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar, refreshKey]);

  const aprobarPublicar = async (id) => {
    if (!window.confirm('¿Aprobar y publicar este borrador?')) return;
    try {
      const r = await api.post(`/campanas-ia/borradores/${id}/aprobar-publicar`);
      if (r.data.publicado_en_meta) {
        toast.success(`Publicado en Meta: ${r.data.meta_campaign_id}`);
      } else {
        toast.warning(`Aprobado (pendiente Meta): ${r.data.razon || JSON.stringify(r.data.meta_error).slice(0, 200)}`);
      }
      cargar();
    } catch (e) {
      toast.error(formatErrorMessage(e));
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar borrador?')) return;
    try {
      await api.delete(`/campanas-ia/borradores/${id}`);
      cargar();
    } catch (e) { toast.error(formatErrorMessage(e)); }
  };

  if (loading) return <Card className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></Card>;

  return (
    <div className="space-y-2">
      {items.length === 0 ? (
        <Card className="p-8 text-center border-slate-200">
          <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600">Sin borradores. Usa el Wizard para crear el primero.</p>
        </Card>
      ) : (
        items.map(b => (
          <Card key={b.id} className="p-3 border-slate-200 hover:shadow-md transition" data-testid={`borrador-${b.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="font-semibold text-slate-900 truncate">{b.nombre}</p>
                  <Badge variant="outline" className="text-xs">{b.pilar}</Badge>
                  <Badge className={
                    b.estado === 'Publicado' ? 'bg-emerald-100 text-emerald-800' :
                    b.estado === 'Aprobado' ? 'bg-amber-100 text-amber-800' :
                    'bg-slate-100 text-slate-700'
                  }>{b.estado}</Badge>
                  {b.requiere_publicacion_manual && <Badge className="bg-rose-100 text-rose-800 text-xs">Pub. manual</Badge>}
                </div>
                <p className="text-xs text-slate-600">
                  {b.objetivo} · {fmtCLP(b.presupuesto_clp)} · {b.duracion_dias}d · {b.formato_sugerido}
                </p>
                {b.meta_campaign_id && <p className="text-xs text-emerald-700 mt-1">Meta ID: {b.meta_campaign_id}</p>}
                {b.meta_error && <p className="text-xs text-rose-700 mt-1 truncate" title={typeof b.meta_error === 'string' ? b.meta_error : JSON.stringify(b.meta_error)}>⚠ {typeof b.meta_error === 'string' ? b.meta_error.slice(0, 100) : 'Ver detalles'}</p>}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setDetalleId(b.id)}><Eye className="w-3.5 h-3.5" /></Button>
                {b.estado === 'Borrador' && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => aprobarPublicar(b.id)} data-testid={`btn-aprobar-${b.id}`}>
                    <Rocket className="w-3.5 h-3.5 mr-1" /> Aprobar y Publicar
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => eliminar(b.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          </Card>
        ))
      )}
      {detalleId && <DetalleBorradorModal id={detalleId} onClose={() => setDetalleId(null)} />}
    </div>
  );
};

const DetalleBorradorModal = ({ id, onClose }) => {
  const [b, setB] = useState(null);
  useEffect(() => { api.get(`/campanas-ia/borradores/${id}`).then(r => setB(r.data)); }, [id]);
  if (!b) return null;
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{b.nombre}</DialogTitle>
          <DialogDescription>{b.objetivo} · {b.pilar} · {b.estado}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid md:grid-cols-3 gap-2">
            {(b.copys || []).map((c, i) => (
              <Card key={i} className="p-2 bg-slate-50">
                <Badge className="mb-1 text-xs">{c.tipo}</Badge>
                <p className="text-sm font-semibold">{c.headline}</p>
                <p className="text-xs text-slate-700 mt-1">{c.body}</p>
                <p className="text-xs text-indigo-700 mt-1">CTA: {c.cta}</p>
              </Card>
            ))}
          </div>
          <div className="text-xs space-y-1">
            <p><span className="font-semibold">Presupuesto:</span> {fmtCLP(b.presupuesto_clp)} · <span className="font-semibold">Duración:</span> {b.duracion_dias} días</p>
            <p><span className="font-semibold">Ubicaciones:</span> {(b.ubicaciones || []).join(', ')}</p>
            <p><span className="font-semibold">Formato:</span> {b.formato_sugerido}</p>
            {b.contexto_legal_usado && <p><span className="font-semibold">Contexto legal:</span> {b.contexto_legal_usado}</p>}
            {(b.palabras_clave_negativas || []).length > 0 && <p><span className="font-semibold">Palabras negativas:</span> {b.palabras_clave_negativas.join(', ')}</p>}
          </div>
          <pre className="text-xs bg-slate-50 p-2 rounded overflow-auto max-h-40">{JSON.stringify(b.segmentacion, null, 2)}</pre>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cerrar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CerebroIA;
